import {Component, inject, linkedSignal, signal, viewChild} from '@angular/core';
import {DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {Capability} from '../../../../classes/models/employee/Capability';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {CheckboxItem} from '../../../../shared/classes/CheckboxItem';
import {ToastrService} from 'ngx-toastr';
import {CapabilityType, capabilityTypeValues} from '../../../../classes/enums/CapabilityType';
import {enumParse} from '../../../../pipes/enum-parse.pipe';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {DataService} from '../../../../services/store/data.service';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {EmployeeCapabilityService} from '../../../../services/http/employee/employee-capability.service';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/**
 * Data required to open the CapabilityFormComponent modal.
 */
export interface CapabilityFormData {
  employeePositionId: EmployeePositionId;
  previousCapability?: Capability;
  weekExists?: boolean;
}

/**
 * This component creates a form in a modal for creating or updating capabilities.
 */
@Component({
  selector: 'app-capability-form',
  templateUrl: './capability-form.component.html',
  styleUrls: ['./capability-form.component.less'],
  imports: [SHARED_IMPORTS]
})
export class CapabilityFormComponent {

  private employeeCapabilityService = inject(EmployeeCapabilityService);
  private toastr = inject(ToastrService);
  private ruleCheckerService = inject(RuleCheckerService);
  private dataService = inject(DataService);
  private destroyRef = inject(DestroyRef);

  /** A modal component from HTML. */
  modalComponent = viewChild.required<ModalComponent>('modalComponent');
  /** Identifiers of an employee and position. */
  employeePositionId = signal<EmployeePositionId>(undefined!);
  /** Previous employee capability. */
  previousCapability = signal<Capability | undefined>(undefined);
  /** `True` if there are records of the selected week in backend. */
  weekExists = signal<boolean>(false);

  /** `True` if request sending to backend is in progress. */
  loading = signal(false);
  /** Form input signal of the start date, derived from previous capability. */
  selectedStartDate = linkedSignal(() => this.previousCapability()?.startDate ?? '');
  /** Form input signal of capability types, derived from previous capability. */
  selectedCapabilityTypes = linkedSignal<string[]>(() => this.previousCapability()?.types ?? []);
  /** An array containing data of which checkboxes are checked, and which are not. */
  capabilityTypeOptions = capabilityTypeValues().map(type => new CheckboxItem(type, enumParse(type)));
  /** The first date from which capability data can be edited. */
  editDate = this.dataService.editDate;

  static open(modalService: NgbModal, data: CapabilityFormData): NgbModalRef {
    const modalRef = modalService.open(CapabilityFormComponent);
    const instance = modalRef.componentInstance as CapabilityFormComponent;
    instance.employeePositionId.set(data.employeePositionId);
    instance.previousCapability.set(data.previousCapability);
    instance.weekExists.set(data.weekExists ?? false);
    return modalRef;
  }

  /**
   * Updates capability types form values.
   * @param types capability types to be set in the form.
   */
  onCapabilityTypesChange(types: string[]): void {
    this.selectedCapabilityTypes.set(types);
  }

  /**
   * Sends request to backend to create or update capability.
   */
  onSubmit(): void {
    this.loading.set(true);

    const types = this.selectedCapabilityTypes().map(type => CapabilityType[type]);
    const capability = new Capability(types, this.selectedStartDate());

    if (this.weekExists()) {
      this.editCapability(capability);
    } else {
      this.addCapability(capability);
    }
  }

  private addCapability(capability: Capability): void {
    this.employeeCapabilityService.addCapability(this.employeePositionId(), capability)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent().activeModal.close();
                this.loading.set(false);
                this.toastr.success('Capabilities successfully created');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }

  private editCapability(capability: Capability): void {
    this.employeeCapabilityService.editCapability(this.employeePositionId(), capability)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent().activeModal.close();
                this.loading.set(false);
                this.toastr.success('Capabilities successfully updated');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}